/**
 * Canonical URL path for a listing.
 *
 * CommonJS on purpose. Two very different consumers need identical output:
 * the frontend (bundled by webpack, which imports CJS happily) and
 * api/scripts/generate-sitemap.js (plain Node on the server, which cannot
 * import an ES module). Duplicating the logic would let the sitemap and the
 * site drift apart, which is exactly the sort of mismatch that wastes crawl
 * budget.
 *
 * SHAPE: /property/<slug>-<24-hex-id>
 *
 *   /property/1-bed-flat-vantage-house-weir-road-wimbledon-6a8561259f8e6a3960f3acd5
 *
 * The id stays in the URL, at the end, and remains the only thing used for
 * lookup. That is what makes this safe to ship on top of 281 already-live
 * URLs: the old bare-id form still resolves, because the id is extracted with
 * a regex rather than by position. Nothing 404s, ever, including links in
 * emails sent years ago.
 */

/** Lowercase, ASCII, hyphen-separated, trimmed to a sane length. */
function slugify(text) {
    return String(text || "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        // A few displayAddress values contain a whole sentence rather than an
        // address, which would otherwise produce an absurd URL.
        .slice(0, 60)
        .replace(/-+$/, "");
}

/** The path a listing should live at. */
function propertyPath(property) {
    if (!property || !property._id) return "/";

    const id = String(property._id);
    const beds = property.bedrooms ? property.bedrooms + " bed" : "";
    const slug = slugify(
        [beds, property.type, property.displayAddress, property.location].filter(Boolean).join(" ")
    );

    return "/property/" + (slug ? slug + "-" : "") + id;
}

/**
 * Pull the listing id back out of a path segment.
 *
 * Matches the trailing 24-hex-character Mongo id, so it works for both
 * `<slug>-<id>` and a bare `<id>`. Returns null for anything else, which the
 * caller treats as a 404 rather than querying with junk.
 */
function extractPropertyId(segment) {
    const match = String(segment || "").match(/([0-9a-f]{24})$/i);
    return match ? match[1] : null;
}

module.exports = { slugify, propertyPath, extractPropertyId };
